import type { CSSProperties, ReactNode } from 'react';
import { illegalRedAdjustmentReason, type RedAdjustmentDelta, type ScoreState } from '@aiscoreboard/contracts';
import { accentOf, frameVars } from '../lib/accent';
import { useT } from '../lib/i18n';

interface Props {
  state: ScoreState;
  /** The square shared with the QR code, so the two blocks match exactly. */
  className?: string;
  /** Drawn without glass of its own, because it shares a card with the frames. */
  flat?: boolean;
  onAdjustReds?: (delta: RedAdjustmentDelta) => void;
}

/**
 * The break in progress, in the middle of the board.
 *
 * This is the number a room actually watches. A frame's points belong to a
 * player and live in that player's panel; the break belongs to *the moment*,
 * and it was previously a small yellow chip inside whichever panel happened to
 * be active —which meant the one figure everybody was following moved sides
 * every visit and was smaller than the frame count nobody was following.
 *
 * Underneath it sits what is still on the table, and what it is worth. Those
 * numbers are the whole tactical picture: the break says what this visit is
 * worth, the points remaining say whether it is enough, and the gap says how
 * much of it has to be found. The ball on used to be the third column and it
 * was the one figure here nobody needed —the pad below already refuses every
 * ball but the legal one, so the board was spending a column to repeat an
 * answer it was already enforcing. A snooker frame is decided by the
 * difference, not by the two totals, and the difference is the one number the
 * board never actually showed.
 *
 * The best-of caption used to close this block. It has moved up to head the
 * frame score, where it belongs —it says what the frames are being played to,
 * and it was the only line here that had nothing to do with the break.
 */
export function BreakBlock({ state, className = '', flat = false, onAdjustReds }: Props) {
  const t = useT();
  const accent = accentOf(state.activePlayer);
  const running = state.currentBreak > 0;
  const exhausted = state.pointsAvailable === 0;

  // Signed towards whoever is ahead, so the number carries the leader's colour
  // and needs no second label to say which way it points. Level is a nought in
  // plain white: `+0` and `-0` are both wrong and both look like a bug.
  const gap = state.scores[0] - state.scores[1];
  const leader = gap === 0 ? null : gap > 0 ? 0 : 1;
  const gapColor = leader === null ? undefined : accentOf(leader).hex;

  const frame = frameVars(accent.rgb, running) as CSSProperties;

  return (
    <div
      data-break-block
      className={`brk-box flex flex-col items-center justify-between gap-1 ${
        flat ? 'w-full px-3 pb-2' : 'hud-frame p-2.5'
      } ${className}`}
      style={flat ? undefined : frame}
    >
      {/* Plain lettering, not a lozenge inside a lozenge. This is the caption
          for the number under it, and a caption that needs its own frame to be
          found is a caption in the wrong place. */}
      <span
        className="hud-label shrink-0 whitespace-nowrap"
        style={running ? { color: accent.hex } : undefined}
      >
        {t('match.breakLabel')}
      </span>

      {/* The break set the same way as the two scores either side of it. It
          is the same *kind* of thing they are —points, in play, changing —so
          it has to be drawn the same way, or the eye files it as a separate
          instrument and stops comparing it with them. The frame score keeps
          its segments precisely because it is a different kind of thing. */}
      <div className="fit-box h-[34cqh] min-h-[3rem] w-full shrink-0">
        <span
          className="num-lg block leading-none"
          style={{
            fontSize: `min(96cqh, ${Math.min(72, 140 / String(state.currentBreak).length)}cqw)`,
            color: running ? accent.hex : 'rgba(255,255,255,0.35)',
          }}
        >
          {state.currentBreak}
        </span>
      </div>

      {/* What is left on the table, and how much of it is needed.
          Each readout now stays on one line so the label and value are read as
          a single fact instead of two separate rows the eye has to stitch. */}
      <div className="brk-extra w-full shrink-0 px-2">
        {/* The reds carry the ball's own colour, lightened enough to hold at
            this size on glass. It is the one readout here that names a thing
            on the table rather than an abstraction, so it is the one that can
            take the object's colour without inventing a code. */}
        <Readout
          label={t('match.redsLabel')}
          value={state.redsRemaining}
          color={state.redsRemaining > 0 ? '#ff6b61' : undefined}
          beforeValue={onAdjustReds && (
            <button type="button" className="step-btn h-7 w-7 shrink-0 text-base"
              aria-label={t('match.redsDecrease')} title={t('match.redsAdjustHint')}
              disabled={illegalRedAdjustmentReason(state, -1) !== null}
              onClick={() => onAdjustReds(-1)}>−</button>
          )}
          afterValue={onAdjustReds && (
            <button type="button" className="step-btn h-7 w-7 shrink-0 text-base"
              aria-label={t('match.redsIncrease')} title={t('match.redsAdjustHint')}
              disabled={illegalRedAdjustmentReason(state, 1) !== null}
              onClick={() => onAdjustReds(1)}>+</button>
          )}
        />
        <Readout label={t('match.remainingLabel')} value={state.pointsAvailable} alert={exhausted} />
        <Readout
          label={t('match.gapLabel')}
          value={leader === null ? 0 : `${Math.abs(gap)}`}
          color={gapColor}
        />
      </div>
    </div>
  );
}

function Readout({
  label,
  value,
  alert = false,
  color,
  beforeValue,
  afterValue,
}: {
  label: string;
  value: number | string;
  alert?: boolean;
  color?: string;
  beforeValue?: ReactNode;
  afterValue?: ReactNode;
}) {
  return (
    <span className="brk-row grid w-full min-w-0 grid-cols-[minmax(0,1fr)_2.5rem_1.75rem] items-center gap-x-1 border-t border-white/[0.07] py-2">
      <span className="flex min-w-0 items-center justify-between gap-1 text-left">
        <span data-readout-label className="truncate whitespace-nowrap text-left text-[0.76rem] font-semibold leading-none tracking-[0.04em] text-white/74">
          {label}
        </span>
        {beforeValue}
      </span>
      <span
        className={`num whitespace-nowrap text-center text-[1.08rem] font-semibold
                    leading-none tabular-nums sm:text-[1.14rem] ${
                      alert ? 'text-sys-yellow' : color ? '' : 'text-white/88'
                    }`}
        style={color && !alert ? { color } : undefined}
      >
        {value}
      </span>
      <span className="flex justify-center">{afterValue}</span>
    </span>
  );
}
